import {
  EmailTemplate,
  EmailTemplateChannel,
  getChannelRecipients,
  isChannelEnabled,
} from '@/lib/admin/email-templates/models/email-template.model';

/** Colonne ordinabili. `content` è subject o notification_message a seconda del canale. */
export type EmailTemplateSortKey =
  | 'template_key'
  | 'description'
  | 'content'
  | 'status'
  | 'updated_at'
  | 'created_at';

export interface EmailTemplateSort {
  key: EmailTemplateSortKey;
  direction: 'asc' | 'desc';
}

/** Testo principale della riga nel canale corrente. */
export const getChannelContent = (
  template: EmailTemplate,
  channel: EmailTemplateChannel
): string =>
  (channel === 'email' ? template.subject : template.notification_message) ?? '';

/**
 * Ricerca istantanea case-insensitive su chiave, descrizione, contenuto del canale
 * e destinatari (token grezzi: è ciò che l'admin vede in tabella).
 */
export const filterTemplates = (
  templates: EmailTemplate[],
  search: string,
  channel: EmailTemplateChannel
): EmailTemplate[] => {
  const needle = search.trim().toLowerCase();
  if (!needle) return templates;

  return templates.filter((template) => {
    const haystack = [
      template.template_key,
      template.description ?? '',
      getChannelContent(template, channel),
      getChannelRecipients(template, channel).join(' '),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
};

const compareStrings = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

/** Le date mancanti finiscono in fondo a prescindere dalla direzione. */
const compareDates = (a: string | null, b: string | null) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return new Date(a).getTime() - new Date(b).getTime();
};

export const sortTemplates = (
  templates: EmailTemplate[],
  sort: EmailTemplateSort,
  channel: EmailTemplateChannel
): EmailTemplate[] => {
  const factor = sort.direction === 'asc' ? 1 : -1;

  return [...templates].sort((a, b) => {
    switch (sort.key) {
      case 'template_key':
        return factor * compareStrings(a.template_key, b.template_key);
      case 'description':
        return factor * compareStrings(a.description ?? '', b.description ?? '');
      case 'content':
        return (
          factor * compareStrings(getChannelContent(a, channel), getChannelContent(b, channel))
        );
      case 'status':
        return (
          factor *
          (Number(isChannelEnabled(a, channel)) - Number(isChannelEnabled(b, channel)))
        );
      case 'updated_at':
        return factor * compareDates(a.updated_at, b.updated_at);
      case 'created_at':
        return factor * compareDates(a.created_at, b.created_at);
      default:
        return 0;
    }
  });
};
